import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Package, Pill, Truck, Clock, CheckCircle, AlertCircle, Search,
  Plus, RefreshCw, Building2, Phone, Mail, TrendingUp, DollarSign,
  Activity, Users, FileText, Filter, Download, Eye, ChevronRight,
  Stethoscope, Heart, Bed, Wind, Wheelchair, Bath, Droplets, Syringe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DMEPharmacyDashboard = () => {
  const [activeTab, setActiveTab] = useState('dme');
  const [dmeData, setDmeData] = useState(null);
  const [pharmacyData, setPharmacyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showRxDialog, setShowRxDialog] = useState(false);
  const [orderType, setOrderType] = useState('dme');
  
  // Form states
  const [newOrder, setNewOrder] = useState({
    patient_id: '',
    items: [],
    vendor_id: '',
    priority: 'routine',
    delivery_instructions: '',
    notes: ''
  });
  
  const [newRx, setNewRx] = useState({
    patient_id: '',
    medications: [],
    pharmacy_id: '',
    prescriber_name: '',
    priority: 'routine',
    notes: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [dmeRes, pharmacyRes] = await Promise.all([
        fetch(`${API_URL}/api/phase57/dme/dashboard`, { headers }),
        fetch(`${API_URL}/api/phase57/pharmacy/dashboard`, { headers })
      ]);
      
      if (dmeRes.ok) {
        const dmeJson = await dmeRes.json();
        setDmeData(dmeJson);
      } else {
        // Use sample data when not authenticated or API error
        setDmeData(generateSampleDMEData());
      }
      
      if (pharmacyRes.ok) {
        const pharmacyJson = await pharmacyRes.json();
        setPharmacyData(pharmacyJson);
      } else {
        // Use sample data when not authenticated or API error
        setPharmacyData(generateSamplePharmacyData());
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use sample data for demo
      setDmeData(generateSampleDMEData());
      setPharmacyData(generateSamplePharmacyData());
    } finally {
      setLoading(false);
    }
  };

  const generateSampleDMEData = () => ({
    stats: {
      total_orders: 342,
      orders_today: 7,
      orders_this_week: 28,
      pending_orders: 12,
      in_transit: 8,
      delivered_today: 4
    },
    recent_orders: [
      { id: '1', order_number: 'DME-20260122-ABC123', patient_name: 'John Smith', status: 'shipped', priority: 'routine', vendor: { name: 'Medline Industries', logo: '🏭' }, items: [{ name: 'Full-Electric Hospital Bed', quantity: 1 }], created_at: new Date().toISOString() },
      { id: '2', order_number: 'DME-20260122-DEF456', patient_name: 'Mary Johnson', status: 'processing', priority: 'urgent', vendor: { name: 'Rotech Healthcare', logo: '💨' }, items: [{ name: 'Portable Oxygen Concentrator', quantity: 1 }], created_at: new Date().toISOString() },
      { id: '3', order_number: 'DME-20260121-GHI789', patient_name: 'Robert Davis', status: 'delivered', priority: 'routine', vendor: { name: 'Lincare Holdings', logo: '🫁' }, items: [{ name: 'Standard Wheelchair', quantity: 1 }], created_at: new Date(Date.now() - 86400000).toISOString() }
    ],
    vendor_stats: [
      { vendor_id: 'medline', vendor_name: 'Medline Industries', logo: '🏭', orders_count: 145, on_time_rate: 94.5, avg_delivery_days: 1.8, api_status: 'connected' },
      { vendor_id: 'rotech', vendor_name: 'Rotech Healthcare', logo: '💨', orders_count: 89, on_time_rate: 97.8, avg_delivery_days: 0.8, api_status: 'connected' },
      { vendor_id: 'lincare', vendor_name: 'Lincare Holdings', logo: '🫁', orders_count: 67, on_time_rate: 96.3, avg_delivery_days: 0.9, api_status: 'connected' },
      { vendor_id: 'apria', vendor_name: 'Apria Healthcare', logo: '🏠', orders_count: 41, on_time_rate: 93.8, avg_delivery_days: 1.6, api_status: 'connected' }
    ],
    category_stats: [
      { category: 'beds', name: 'Hospital Beds', icon: '🛏️', count: 78 },
      { category: 'oxygen', name: 'Oxygen Equipment', icon: '💨', count: 124 },
      { category: 'wheelchairs', name: 'Wheelchairs', icon: '🦽', count: 56 },
      { category: 'lifts', name: 'Patient Lifts', icon: '🏋️', count: 34 },
      { category: 'bathroom_safety', name: 'Bathroom Safety', icon: '🚿', count: 45 }
    ]
  });

  const generateSamplePharmacyData = () => ({
    stats: {
      total_prescriptions: 856,
      prescriptions_today: 12,
      pending_prescriptions: 18,
      delivered_today: 8,
      refills_needed: 14
    },
    recent_prescriptions: [
      { id: '1', rx_number: 'RX-20260122-ABC123', patient_name: 'John Smith', status: 'filling', priority: 'routine', pharmacy: { name: 'Enclara Pharmacia', logo: '💊' }, medications: [{ name: 'Morphine Sulfate Oral Solution 20mg/5mL', quantity: 1 }], created_at: new Date().toISOString() },
      { id: '2', rx_number: 'RX-20260122-DEF456', patient_name: 'Mary Johnson', status: 'shipped', priority: 'urgent', pharmacy: { name: 'BrightSpring Health Pharmacy', logo: '☀️' }, medications: [{ name: 'Lorazepam 1mg', quantity: 30 }], created_at: new Date().toISOString() },
      { id: '3', rx_number: 'RX-20260121-GHI789', patient_name: 'Robert Davis', status: 'delivered', priority: 'routine', pharmacy: { name: 'PharMerica', logo: '🏪' }, medications: [{ name: 'Ondansetron ODT 4mg', quantity: 20 }], created_at: new Date(Date.now() - 86400000).toISOString() }
    ],
    pharmacy_stats: [
      { pharmacy_id: 'enclara', pharmacy_name: 'Enclara Pharmacia', logo: '💊', prescriptions_count: 312, on_time_rate: 98.2, avg_delivery_hours: 18, api_status: 'connected' },
      { pharmacy_id: 'brightspring', pharmacy_name: 'BrightSpring Health', logo: '☀️', prescriptions_count: 234, on_time_rate: 99.1, avg_delivery_hours: 8, api_status: 'connected' },
      { pharmacy_id: 'pharmerica', pharmacy_name: 'PharMerica', logo: '🏪', prescriptions_count: 189, on_time_rate: 97.1, avg_delivery_hours: 16, api_status: 'connected' },
      { pharmacy_id: 'optum', pharmacy_name: 'Optum Specialty', logo: '⚕️', prescriptions_count: 121, on_time_rate: 95.6, avg_delivery_hours: 20, api_status: 'connected' }
    ],
    class_stats: [
      { class: 'opioid', name: 'Opioid Analgesics', icon: '💊', prescriptions_count: 245 },
      { class: 'antiemetic', name: 'Antiemetics', icon: '🤢', prescriptions_count: 156 },
      { class: 'laxative', name: 'Laxatives', icon: '🚽', prescriptions_count: 189 },
      { class: 'benzodiazepine', name: 'Benzodiazepines', icon: '💤', prescriptions_count: 134 },
      { class: 'anticholinergic', name: 'Anticholinergics', icon: '💧', prescriptions_count: 98 }
    ]
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      approved: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
      processing: { color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw },
      filling: { color: 'bg-orange-100 text-orange-800', icon: RefreshCw },
      shipped: { color: 'bg-purple-100 text-purple-800', icon: Truck },
      ready: { color: 'bg-teal-100 text-teal-800', icon: Package },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
      verified: { color: 'bg-cyan-100 text-cyan-800', icon: CheckCircle }
    };
    
    const config = statusConfig[status] || statusConfig.submitted;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      routine: 'bg-gray-100 text-gray-800',
      urgent: 'bg-orange-100 text-orange-800',
      stat: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[priority] || colors.routine}>{priority.toUpperCase()}</Badge>;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold mt-1 text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center mt-2 text-sm font-medium">
            <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
            <span className="text-green-700">{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const VendorCard = ({ vendor, type }) => (
    <Card className="hover:shadow-md transition-shadow cursor-pointer">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-2xl">{vendor.logo}</span>
          <div className="flex-1">
            <h4 className="font-semibold text-sm text-slate-900">{type === 'dme' ? vendor.vendor_name : vendor.pharmacy_name}</h4>
            <div className="flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${vendor.api_status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className="text-xs text-slate-600 font-medium">{vendor.api_status}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-slate-900">{type === 'dme' ? vendor.orders_count : vendor.prescriptions_count}</p>
            <p className="text-xs text-slate-600 font-medium">Orders</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-700">{vendor.on_time_rate}%</p>
            <p className="text-xs text-slate-600 font-medium">On-Time</p>
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900">{type === 'dme' ? `${vendor.avg_delivery_days}d` : `${vendor.avg_delivery_hours}h`}</p>
            <p className="text-xs text-slate-600 font-medium">Avg Delivery</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const OrderRow = ({ order, type }) => (
    <tr className="hover:bg-gray-50 cursor-pointer" data-testid={`order-row-${order.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{order.vendor?.logo || order.pharmacy?.logo}</span>
          <div>
            <p className="font-medium text-sm">{type === 'dme' ? order.order_number : order.rx_number}</p>
            <p className="text-xs text-gray-500">{order.vendor?.name || order.pharmacy?.name}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-medium">{order.patient_name}</p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm">{order.items?.[0]?.name || order.medications?.[0]?.name}</p>
        {(order.items?.length > 1 || order.medications?.length > 1) && (
          <p className="text-xs text-gray-500">+{(order.items?.length || order.medications?.length) - 1} more</p>
        )}
      </td>
      <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
      <td className="px-4 py-3">{getPriorityBadge(order.priority)}</td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
      </td>
      <td className="px-4 py-3">
        <Button variant="ghost" size="sm">
          <Eye className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );

  const CategoryCard = ({ category }) => (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
      <span className="text-2xl">{category.icon}</span>
      <div className="flex-1">
        <p className="font-medium text-sm">{category.name}</p>
        <p className="text-xs text-gray-500">{category.count || category.prescriptions_count} orders</p>
      </div>
      <ChevronRight className="h-4 w-4 text-gray-400" />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading DME & Pharmacy data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dme-pharmacy-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">DME & Pharmacy Management</h1>
              <p className="text-sm text-gray-500 mt-1">Manage equipment orders and prescriptions</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboardData} data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-order-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create New Order</DialogTitle>
                    <DialogDescription>Select the type of order you want to create</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <Button
                      variant="outline"
                      className="h-24 flex flex-col items-center justify-center gap-2"
                      onClick={() => { setOrderType('dme'); }}
                    >
                      <Package className="h-8 w-8 text-blue-600" />
                      <span>DME Order</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-24 flex flex-col items-center justify-center gap-2"
                      onClick={() => { setOrderType('rx'); setShowRxDialog(true); setShowOrderDialog(false); }}
                    >
                      <Pill className="h-8 w-8 text-green-600" />
                      <span>Prescription</span>
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dme" className="flex items-center gap-2" data-testid="dme-tab">
              <Package className="h-4 w-4" />
              DME Equipment
            </TabsTrigger>
            <TabsTrigger value="pharmacy" className="flex items-center gap-2" data-testid="pharmacy-tab">
              <Pill className="h-4 w-4" />
              Pharmacy
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="analytics-tab">
              <Activity className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* DME Tab */}
          <TabsContent value="dme" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard title="Total Orders" value={dmeData?.stats?.total_orders || 0} icon={Package} color="blue" />
              <StatCard title="Orders Today" value={dmeData?.stats?.orders_today || 0} icon={Plus} color="green" />
              <StatCard title="This Week" value={dmeData?.stats?.orders_this_week || 0} icon={TrendingUp} color="indigo" />
              <StatCard title="Pending" value={dmeData?.stats?.pending_orders || 0} icon={Clock} color="yellow" />
              <StatCard title="In Transit" value={dmeData?.stats?.in_transit || 0} icon={Truck} color="purple" />
              <StatCard title="Delivered Today" value={dmeData?.stats?.delivered_today || 0} icon={CheckCircle} color="green" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Vendors */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    DME Vendors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {dmeData?.vendor_stats?.map((vendor) => (
                    <VendorCard key={vendor.vendor_id} vendor={vendor} type="dme" />
                  ))}
                </CardContent>
              </Card>

              {/* Recent Orders */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Recent DME Orders
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input 
                        placeholder="Search orders..." 
                        className="pl-9 w-48"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm text-gray-500">
                          <th className="px-4 py-2">Order</th>
                          <th className="px-4 py-2">Patient</th>
                          <th className="px-4 py-2">Items</th>
                          <th className="px-4 py-2">Status</th>
                          <th className="px-4 py-2">Priority</th>
                          <th className="px-4 py-2">Date</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dmeData?.recent_orders?.map((order) => (
                          <OrderRow key={order.id} order={order} type="dme" />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Equipment Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {dmeData?.category_stats?.map((category) => (
                    <CategoryCard key={category.category} category={category} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pharmacy Tab */}
          <TabsContent value="pharmacy" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard title="Total Prescriptions" value={pharmacyData?.stats?.total_prescriptions || 0} icon={Pill} color="green" />
              <StatCard title="Today's Rx" value={pharmacyData?.stats?.prescriptions_today || 0} icon={Plus} color="blue" />
              <StatCard title="Pending" value={pharmacyData?.stats?.pending_prescriptions || 0} icon={Clock} color="yellow" />
              <StatCard title="Delivered Today" value={pharmacyData?.stats?.delivered_today || 0} icon={CheckCircle} color="green" />
              <StatCard title="Refills Needed" value={pharmacyData?.stats?.refills_needed || 0} icon={RefreshCw} color="orange" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Pharmacies */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Stethoscope className="h-5 w-5" />
                    Partner Pharmacies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pharmacyData?.pharmacy_stats?.map((pharmacy) => (
                    <VendorCard key={pharmacy.pharmacy_id} vendor={pharmacy} type="pharmacy" />
                  ))}
                </CardContent>
              </Card>

              {/* Recent Prescriptions */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Recent Prescriptions
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input 
                        placeholder="Search prescriptions..." 
                        className="pl-9 w-48"
                      />
                    </div>
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm text-gray-500">
                          <th className="px-4 py-2">Rx #</th>
                          <th className="px-4 py-2">Patient</th>
                          <th className="px-4 py-2">Medications</th>
                          <th className="px-4 py-2">Status</th>
                          <th className="px-4 py-2">Priority</th>
                          <th className="px-4 py-2">Date</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pharmacyData?.recent_prescriptions?.map((rx) => (
                          <OrderRow key={rx.id} order={rx} type="pharmacy" />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Drug Classes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Drug Classes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {pharmacyData?.class_stats?.map((drugClass) => (
                    <CategoryCard key={drugClass.class} category={drugClass} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* DME Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    DME Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Monthly Avg Spend</p>
                        <p className="text-2xl font-bold text-blue-600">$32,450</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-blue-400" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Top Equipment Categories</p>
                      {[
                        { name: 'Oxygen Equipment', value: 35, color: 'bg-blue-500' },
                        { name: 'Hospital Beds', value: 28, color: 'bg-indigo-500' },
                        { name: 'Wheelchairs', value: 18, color: 'bg-purple-500' },
                        { name: 'Patient Lifts', value: 12, color: 'bg-pink-500' },
                        { name: 'Other', value: 7, color: 'bg-gray-400' }
                      ].map((item) => (
                        <div key={item.name} className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className="flex justify-between text-sm mb-1">
                              <span>{item.name}</span>
                              <span>{item.value}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.value}%` }}></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pharmacy Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="h-5 w-5 text-green-600" />
                    Pharmacy Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Monthly Avg Spend</p>
                        <p className="text-2xl font-bold text-green-600">$68,920</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-green-400" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Top Drug Classes</p>
                      {[
                        { name: 'Opioid Analgesics', value: 38, color: 'bg-green-500' },
                        { name: 'Laxatives', value: 22, color: 'bg-emerald-500' },
                        { name: 'Antiemetics', value: 18, color: 'bg-teal-500' },
                        { name: 'Benzodiazepines', value: 14, color: 'bg-cyan-500' },
                        { name: 'Other', value: 8, color: 'bg-gray-400' }
                      ].map((item) => (
                        <div key={item.name} className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className="flex justify-between text-sm mb-1">
                              <span>{item.name}</span>
                              <span>{item.value}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.value}%` }}></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Vendor Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Vendor Performance Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="px-4 py-3">Vendor</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Total Orders</th>
                        <th className="px-4 py-3">On-Time Rate</th>
                        <th className="px-4 py-3">Avg Delivery</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dmeData?.vendor_stats?.map((vendor) => (
                        <tr key={vendor.vendor_id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{vendor.logo}</span>
                              <span className="font-medium">{vendor.vendor_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3"><Badge variant="outline">DME</Badge></td>
                          <td className="px-4 py-3">{vendor.orders_count}</td>
                          <td className="px-4 py-3">
                            <span className={vendor.on_time_rate >= 95 ? 'text-green-600' : vendor.on_time_rate >= 90 ? 'text-yellow-600' : 'text-red-600'}>
                              {vendor.on_time_rate}%
                            </span>
                          </td>
                          <td className="px-4 py-3">{vendor.avg_delivery_days} days</td>
                          <td className="px-4 py-3">
                            <Badge className="bg-green-100 text-green-800">{vendor.api_status}</Badge>
                          </td>
                        </tr>
                      ))}
                      {pharmacyData?.pharmacy_stats?.map((pharmacy) => (
                        <tr key={pharmacy.pharmacy_id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{pharmacy.logo}</span>
                              <span className="font-medium">{pharmacy.pharmacy_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3"><Badge variant="outline" className="bg-green-50">Pharmacy</Badge></td>
                          <td className="px-4 py-3">{pharmacy.prescriptions_count}</td>
                          <td className="px-4 py-3">
                            <span className={pharmacy.on_time_rate >= 95 ? 'text-green-600' : pharmacy.on_time_rate >= 90 ? 'text-yellow-600' : 'text-red-600'}>
                              {pharmacy.on_time_rate}%
                            </span>
                          </td>
                          <td className="px-4 py-3">{pharmacy.avg_delivery_hours} hours</td>
                          <td className="px-4 py-3">
                            <Badge className="bg-green-100 text-green-800">{pharmacy.api_status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DMEPharmacyDashboard;
